import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
} from '@mui/material';
import { toast } from 'react-toastify';
import { CreditCard, ShieldCheck } from 'lucide-react';

function WhatsAppBrandIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm0 18.15c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.32a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c.02 4.54-3.68 8.24-8.22 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.66.31-.22.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.16 1.73 2.64 4.18 3.7.58.25 1.04.4 1.39.51.59.19 1.12.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.22-.16-.47-.28z" />
    </svg>
  );
}

export default function BotPricingDetails({
  bot,
  onActivate,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [activating, setActivating] = useState(false);

  // Pricing structure: One-time activation fee + monthly maintenance fee
  const activationFee = bot.activationFee || 120000;
  const maintenanceFee = bot.maintenanceFee || 30000;
  const totalDueToday = activationFee + maintenanceFee;

  const handleConfirmActivation = async () => {
    setActivating(true);
    try {
      if (onActivate) {
        await onActivate(bot.id);
      }
    } catch (err) {
      toast.error('Failed to initiate Paystack checkout');
      setActivating(false);
    }
  };

  const isWhatsApp = bot.id === 'whatsapp';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {/* 1. Brand Squircle Icon */}
      <Box
        sx={{
          width: { xs: 72, sm: 80 },
          height: { xs: 72, sm: 80 },
          borderRadius: '22px',
          bgcolor: isWhatsApp ? '#25D366' : bot.brandBg,
          color: isWhatsApp ? '#FFFFFF' : bot.brandColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isWhatsApp
            ? '0 10px 24px rgba(37, 211, 102, 0.28)'
            : `0 10px 24px ${bot.brandColor}25`,
        }}
      >
        {isWhatsApp ? <WhatsAppBrandIcon /> : bot.icon}
      </Box>

      {/* 2. Title & Subtitle */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          fontSize: { xs: '2rem', sm: '2.4rem' },
          color: '#111827',
          letterSpacing: '-0.03em',
          mt: 3,
          lineHeight: 1.15,
        }}
      >
        {bot.name}
      </Typography>

      <Typography
        sx={{
          fontSize: { xs: '1.05rem', sm: '1.15rem' },
          color: '#6B7280',
          fontWeight: 400,
          mt: 1,
          lineHeight: 1.4,
        }}
      >
        {bot.description || 'Sell data, airtime and bills in WhatsApp.'}
      </Typography>

      {/* 3. Big Price */}
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: '3.25rem', sm: '3.75rem' },
          color: '#111827',
          letterSpacing: '-0.04em',
          lineHeight: 1.05,
          mt: { xs: 4, sm: 5 },
        }}
      >
        ₦{totalDueToday.toLocaleString()}
      </Typography>

      {/* 4. Price Breakdown */}
      <Typography
        sx={{
          fontSize: { xs: '0.88rem', sm: '0.95rem' },
          color: '#6B7280',
          fontWeight: 400,
          mt: 1,
        }}
      >
        ₦{activationFee.toLocaleString()} one-time setup + ₦{maintenanceFee.toLocaleString()} first month.
      </Typography>

      {/* 5. Apple Blue Pill Button */}
      <Box sx={{ width: '100%', mt: { xs: 4, sm: 5 } }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => setModalOpen(true)}
          sx={{
            py: 1.6,
            borderRadius: '999px',
            fontSize: '1.05rem',
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: '#0071E3',
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#0077ED',
              boxShadow: 'none',
            },
          }}
        >
          Activate
        </Button>
      </Box>

      {/* 6. Footer Disclaimer */}
      <Typography
        sx={{
          fontSize: { xs: '0.82rem', sm: '0.88rem' },
          color: '#86868B',
          mt: 1.75,
        }}
      >
        Then ₦{maintenanceFee.toLocaleString()} a month. Cancel anytime in Billing.
      </Typography>

      {/* Confirmation Dialog */}
      <Dialog
        open={modalOpen}
        onClose={() => !activating && setModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '24px', p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            Activate {bot.name}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ my: 1 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                One-time Setup Fee:
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                ₦{activationFee.toLocaleString()}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                1st Month Maintenance:
              </Typography>
              <Typography variant="subtitle2" fontWeight={600}>
                ₦{maintenanceFee.toLocaleString()}
              </Typography>
            </Stack>

            <Box sx={{ height: 1, bgcolor: 'divider', my: 0.5 }} />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>
                Total Due Today:
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} color="#111827">
                ₦{totalDueToday.toLocaleString()}
              </Typography>
            </Stack>

            <Typography variant="caption" sx={{ color: '#6B7280', textAlign: 'center' }}>
              Recurring maintenance will be ₦{maintenanceFee.toLocaleString()} / month thereafter.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: '14px',
                bgcolor: '#F9FAFB',
                borderColor: '#E5E7EB',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      bgcolor: 'rgba(10, 165, 255, 0.1)',
                      color: '#0AA5FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CreditCard size={18} />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.7rem', fontWeight: 500 }}
                    >
                      Payment Method
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color="#111827"
                      sx={{ fontSize: '0.82rem' }}
                    >
                      Paystack Secure Checkout
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.68rem' }}
                    >
                      Debit Card, Bank Transfer, USSD
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: '999px',
                    bgcolor: '#ECFDF5',
                    color: '#059669',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <ShieldCheck size={12} />
                  Secured
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setModalOpen(false)}
            disabled={activating}
            sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmActivation}
            disabled={activating}
            startIcon={
              activating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CreditCard size={16} />
              )
            }
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#0071E3',
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0077ED', boxShadow: 'none' },
            }}
          >
            {activating
              ? 'Opening Paystack...'
              : `Pay with Paystack • ₦${totalDueToday.toLocaleString()}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

BotPricingDetails.propTypes = {
  bot: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    activationFee: PropTypes.number,
    maintenanceFee: PropTypes.number,
    brandColor: PropTypes.string.isRequired,
    brandBg: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
  }).isRequired,
  onActivate: PropTypes.func.isRequired,
};
